import { gsap } from "gsap/dist/gsap";
import { EaseType } from "./EaseType";
import { TweenVarConvertor } from "./TweenVarConverter";
import { PixiPlugin } from "gsap/PixiPlugin";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
gsap.registerPlugin(PixiPlugin, MotionPathPlugin);

/**
 * A class, used to animate object(s). It also provides a way to call a function after a given delay.
 *
 * @export
 * @class Tween
 * @implements {IWrapper}
 * @implements {ITweenable}
 */
export class Tween implements IWrapper, ITweenable {
    private readonly _gsapTween: gsap.core.Tween;
    private readonly _tweenType: TweenType;
    private readonly _secondVars: TweenVars;

    /**
     * Creates an instance of Tween.
     * ```ts
     * // TO
     * let tweenTo = new Tween(sprite.scale, 1.0, { x: 2.0 });
     * // FROM
     * let tweenFrom = new Tween(sprite.scale, 1.0, { x: 0.1 }, TweenType.From);
     * // FROMTO
     * let tweenFromTo = new Tween(sprite, 0.5, { alpha: 1.0 }, TweenType.FromTo, { alpha: 0.0 });
     * // DELAYED CALL
     * let vars = { delay: 5.0, onComplete: () => { alert("hi"); }, onCompleteParams: [], callbackScope: this };
     * let delayedCall = new Tween(undefined, 0, vars, TweenType.DelayedCall)
     * ```
     * @param {TweenTarget} targets The object(s) to animate. Can not be undefined.
     * @param {number} duration The animation duration in seconds. Can not be negative.
     * @param {TweenVars} vars The animation settings. Can not be undefined.
     * @param {TweenType} [tweenType=TweenType.To] The animation type. Must be a member of TweenType enumeration.
     * @param {TweenVars} [secondVars] The secondary animation settings.
     * Those will be used only for TweenType.FromTo. In this case this arg can not be undefined.
     * @memberof Tween
     */
    constructor(targets: TweenTarget, duration: number, vars: TweenVars, tweenType: TweenType = TweenType.To, secondVars?: TweenVars) {
        if (!targets && tweenType != TweenType.DelayedCall) {
            throw Error("targets can not be undefined!");
        }

        if (duration < 0) {
            throw Error("duration can not be < 0");
        }

        if (!vars) {
            throw Error("vars can not be undefined!");
        }

        if (tweenType < 0 || tweenType > 4) {
            throw Error("tweenType out of range [0-4]. Passed: " + tweenType.toFixed());
        }

        let convertedVars = TweenVarConvertor.convertTweenVars(vars);
        if (!convertedVars.duration) {
            convertedVars.duration = duration;
        }

        switch (tweenType) {
            case TweenType.To: {
                this._gsapTween = gsap.to(targets, convertedVars);
            }

            break;
            case TweenType.From: {
                this._gsapTween = gsap.from(targets, convertedVars);
            }

            break;
            case TweenType.FromTo: {
                if (secondVars) {
                    let convertedSecondVars = TweenVarConvertor.convertTweenVars(secondVars);
                    if (!convertedSecondVars.duration) {
                        convertedSecondVars.duration = duration;
                    }
        
                    this._gsapTween = gsap.fromTo(targets, convertedVars, convertedSecondVars);
                    this._secondVars = secondVars;
                } else {
                    throw Error("secondVars are required, when constructing TweenType.FromTo!");
                }
            }

            break;
            case TweenType.DelayedCall: {
                this._gsapTween = gsap.delayedCall(Number(convertedVars.delay), convertedVars.onComplete, vars.onCompleteParams);
                this._gsapTween.vars.callbackScope = convertedVars.callbackScope;
            }

            break;
            case TweenType.Timeline: {
                throw Error("TweenType.Timeline can not be used, when constructing a Tween!");
            }
        }

        this._tweenType = tweenType;
    }

    /**
     * Creates a tween going from the current object's values TO the given values.
     * 
     * ```ts
     * Tween.to(sprite.position, 1.0, { x: 100 });
     * ```
     *
     * @static
     * @param {TweenTarget} target The object to animate.
     * @param {number} duration The animation duration in seconds.
     * @param {TweenVars} vars The animation settings.
     * @return {*}  {Tween}
     * @memberof Tween
     */
    public static to(target: TweenTarget, duration: number, vars: TweenVars): Tween {
        return new Tween(target, duration, vars, TweenType.To);
    }

    /**
     * Creates a tween going FROM the given values to the current ones.
     * 
     * ```ts
     * Tween.from(sprite.position, 1.0, { x: 200 });
     * ```
     *
     * @static
     * @param {TweenTarget} target The object to animate.
     * @param {number} duration The animation duration in seconds.
     * @param {TweenVars} vars The animation settings.
     * @return {*}  {Tween}
     * @memberof Tween
     */
    public static from(target: TweenTarget, duration: number, vars: TweenVars): Tween {
        return new Tween(target, duration, vars, TweenType.From);
    }

    /**
     * Creates a tween going from given values to given values,
     * regardless the object current values.
     * 
     * ```ts
     * Tween.fromTo(sprite.position, 1.0, { x: 100 }, { x: 200 });
     * ```
     *
     * @static
     * @param {TweenTarget} targets The object to animate or an array of objects to animate together.
     * @param {number} duration The animation duration in seconds.
     * @param {TweenVars} fromVars The start animation settings.
     * @param {TweenVars} toVars The end animation settings.
     * @return {*}  {Tween}
     * @memberof Tween
     */
    public static fromTo(targets: TweenTarget, duration: number, fromVars: TweenVars, toVars: TweenVars): Tween {
        return new Tween(targets, duration, fromVars, TweenType.FromTo, toVars);
    }

    /**
     * Executes a method or function (callback) after a given delay.
     *
     * @static
     * @param {number} delay The time in seconds to wait, before executing the callback.
     * @param {Callback} callback The method or the (arrow) function to execute, when time is up.
     * @param {any[]} [params] The arguments, which will be passed to the callback, when time is up.
     * @param {object} [scope] The function scope, i.e the object, which must refers to 'this' inside the callback, when time is up.
     * @return {*}  {Tween}
     * @memberof Tween
     */
    public static delayedCall(delay: number, callback: Callback, params?: any[], scope?: object): Tween {
        let paramsToUse = params;
        if (params && params.length == 0) {
            paramsToUse = undefined;
        }

        let vars = {
            immediateRender: false,
            lazy: false,
            delay: delay,
            onComplete: callback,
            onReverseComplete: callback,
            onCompleteParams: paramsToUse,
            onReverseCompleteParams: paramsToUse,
            callbackScope: scope,
          };

        return new Tween(undefined, 0, vars, TweenType.DelayedCall);
    }

    /**
     * Kills all the tweens (or specific tweening properties) of a particular object or the delayedCalls to a particular function.
     *
     * @static
     * @param {TweenTarget} targets An object or function, or array of them.
     * @param {{}} [vars] The specific properties to kill.
     * @memberof Tween
     */
    public static killTweensOf(targets: TweenTarget, vars?: {}): void {
        gsap.killTweensOf(targets, vars);
    }

    /**
     * Kills all the tweens of a particular object or the delayedCalls to a particular function.
     *
     * @static
     * @param {TweenTarget} targets An object or function, or array of them.
     * @memberof Tween
     */
    public static killDelayedCallsTo(targets: TweenTarget): void {
        gsap.killTweensOf(targets);
    }

    /**
     * Empties the global timeline of all tweens, timelines, callbacks, and labels.
     *
     * @static
     * @memberof Tween
     */
    public static killAll() {
        gsap.globalTimeline.clear(true);
    }

    /**
     * Gets the internally wrapped object.
     *
     * @return {*}  {*}
     * @memberof Tween
     */
    public getWrappedObject(): any {
        return this._gsapTween;
    }

    /**
     * Gets the type of the tween.
     *
     * @return {*}  {TweenType} The tween type.
     * @memberof Tween
     */
    public getTweenType(): TweenType {
        return this._tweenType;
    }

    /**
     * Subscribes the passed callback to the choosen event type.
     *
     * @param {TweenCallbackType} type The type of the event callback.
     * @param {Callback} callback The method or (arrow) function to execute, when the event is fired.
     * @param {any[]} [params] The arguments to pass to the callback function, when the event is fired.
     * @param {object} [scope] The function scope, i.e the object, which must refers to 'this' inside the callback, when event is fired. 
     * @memberof Tween
     */
    public eventCallback(type: TweenCallbackType, callback: Callback, params?: any[], scope?: object) {
        let paramsToUse = params;
        if (params && params.length == 0) {
            paramsToUse = undefined;
        }

        this._gsapTween.eventCallback(<gsap.CallbackType>(type.toString()), callback, paramsToUse, scope);
    }

    /**
     * Kills the parts of the tween specified. 
     * To kill means to immediately stop the tween, remove it from its parent timeline, and release it for garbage collection.
     *
     * ```js
     * // kills the entire tween
     * tween.kill(); 
     * 
     * // kill all parts of the tween related to the target "myObject" (if the tween has multiple targets, the others will not be affected):
     * tween.kill(myObject);
     * 
     * // kill only the "x" and "y" properties of the tween (all targets):
     * tween.kill(null, "x,y");
     * 
     * // kill only the "x" and "y" properties of tween of the target "myObject":
     * tween.kill(myObject, "x,y");
     * 
     * // kill only the "opacity" properties of the tween of the targets "myObject1" and "myObject2":
     * tween.kill([myObject1, myObject2], "opacity");
     * ```
     *
     * @param {object} [targets] The target(s) to kill.
     * @param {string} [propertiesList] The properties to kill.
     * @memberof Tween
     */
    public kill(targets?: object, propertiesList?: string) {
        this._gsapTween.kill(targets, propertiesList);
    }

    /**
     * Gets the progress of the animation.
     *
     * @type {number}
     * @memberof Tween
     */
    public get progress(): number {
        return this._gsapTween.progress();
    }

    /**
     * Sets the progress of the animation (between 0 and 1).
     *
     * @memberof Tween
     */
    public set progress(value: number) {
        this._gsapTween.progress(value);
    }

    /**
     * Gets the total duration (including repeats) of the given animation.
     *
     * @type {number}
     * @memberof Tween
     */
    public get totalDuration(): number {
        return this._gsapTween.totalDuration();
    }

    /**
     * Sets the total duration (including repeats) of the given animation by time scaling the animation.
     *
     * @memberof Tween
     */
    public set totalDuration(value: number) {
        this._gsapTween.totalDuration(value);
    }

    /**
     * Gets the animation settings.
     *
     * @type {TweenVars}
     * @memberof Tween
     */
    public get vars(): TweenVars {
        return TweenVarConvertor.convertBackTweenVars(this._gsapTween.vars);
    }

    /**
     * Sets the animation settings.
     *
     * @memberof Tween
     */
    public set vars(value: TweenVars) {
        this._gsapTween.vars = TweenVarConvertor.convertTweenVars(value);
    }

    /**
     * Gets the fromTo (second vars).
     * Usefull only for FromTo tween type.
     *
     * @readonly
     * @memberof Tween
     */
    public get secondVars(): TweenVars {
        return this._secondVars;
    }

    /**
     * Returns an array of all of the tween's targets.
     *
     * @readonly
     * @type {unknown[]}
     * @memberof Tween
     */
    public get targets(): unknown[] {
        return this._gsapTween.targets();
    }

    /**
     * Gets the tween time scale.
     *
     * @type {number}
     * @memberof Tween
     */
    public get timeScale(): number {
        return this._gsapTween.timeScale();
    }

    /**
     * Sets the tween time scale.
     *
     * @memberof Tween
     */
    public set timeScale(value: number) {
        this._gsapTween.timeScale(value);
    }

    /**
     * Plays the animation, optionally from the given start time.
     *
     * ```js
     * anim.play(true); // play from current point
     * anim.play(1.5);  // play from the 1.5 second mark
     * ```
     *
     * @param {(number | string | null)} [from]
     * @param {boolean} [suppressEvents]
     * @return {*}  {Tween}
     * @memberof Tween
     */
    public play(from?: number | string, suppressEvents: boolean = false): Tween {
        this._gsapTween.play(from, suppressEvents);
        return this;
    }

    /**
     * Returns true or false based on the active state of the animation.
     * Being active means that the virtual playhead is actively moving across this instance's time span and it is not paused, nor are any of its ancestor timelines.
     *
     * @return {*}  {boolean}
     * @memberof Tween
     */
    public isActive(): boolean {
       return this._gsapTween.isActive();
    }
}

export type Callback = (...args: any[]) => void | Promise<void> | Promise<boolean> | null | object;

export type TweenTarget = string | object | null;

export enum TweenCallbackType {
    onComplete = "onComplete",
    onInterrupt = "onInterrupt",
    onRepeat = "onRepeat",
    onReverseComplete = "onReverseComplete",
    onStart = "onStart",
    onUpdate = "onUpdate"
}

export enum TweenType {
    To = 0,
    From = 1,
    FromTo = 2,
    DelayedCall = 3,
    Timeline = 4
}

export interface CallbackVars {
    /** The scope to be used for all of the callbacks (onStart, onUpdate, onComplete, etc.). The scope is what "this" refers to inside any of the callbacks. */
    callbackScope?: object;

    /**  A () => void that should be called when the animation has completed. */
    onComplete?: Callback;

    /** An Array of parameters to pass the onComplete () => void */
    onCompleteParams?: any[];

    onRepeat?: Callback;

    onRepeatParams?: any[];

    /** A () => void that should be called when the tween has reached its beginning again from the reverse direction. */
    onReverseComplete?: Callback;

    /** An Array of parameters to pass the onReverseComplete () => void. */
    onReverseCompleteParams?: any[];

    /** A () => void that should be called when the tween begins (when its time changes from 0 to some other value which can happen more than once if the tween is restarted multiple times). */
    onStart?: Callback;

    /** An Array of parameters to pass the onStart () => void. */
    onStartParams?: any[];

    /** A () => void that should be called every time the animation updates (on every frame while the animation is active). */
    onUpdate?: Callback;

    /** An Array of parameters to pass the onUpdate () => void. */
    onUpdateParams?: any[];
}

export interface AnimationVars extends CallbackVars {
    /** Any tweenable property */
    [key: string]: any;

    data?: any;    

    id?: string | number;

    inherit?: boolean;

    /** If true, the tween will pause itself immediately upon creation. */
    paused?: boolean;

    repeat?: number;

    repeatDelay?: number;

    repeatRefresh?: boolean;

    reversed?: boolean;

    yoyo?: boolean;
}

export interface TweenVars extends AnimationVars {
    
    /**
     * Amount of delay in seconds (or frames for frames-based tweens) before the animation should begin.
     */
    delay?: number;

    duration?: number | string;

    /** Ease (or () => void or String) - You can choose from various eases to control the rate of change during the animation, giving it a specific "feel". */
    ease?: EaseType;

    /**
     * Controls how (and if) other tweens of the same target are overwritten. There are several modes to choose from, but "auto" is the default (although you can change the default mode using
     * theTweenLite.defaultOverwrite property)
     */
    overwrite?: boolean | "auto";

    /**
     * When a tween renders for the very first time and reads its starting values, GSAP will automatically "lazy render" that particular tick by default, meaning it will try to delay the
     * rendering (writing of values) until the very end of the "tick" cycle which can improve performance because it avoids the read/write/read/write layout thrashing that some browsers do.
     *
     * If you would like to disable lazy rendering for a particular tween, you can set lazy:false. Or, since zero-duration tweens do not lazy-render by default, you can specifically give it
     * permission to lazy-render by setting lazy:true like TweenLite.set(element, {opacity:0, lazy:true});. In most cases, you won't need to set lazy.
     */
    lazy?: boolean;

    startAt?: {};
}

export interface IWrapper {
    getWrappedObject(): any;
}

export interface ITweenable {
    getTweenType(): TweenType;
}